export interface QueryMap {
  [k: string]: string | undefined;
}

/** Returns an object representing the query parameters for a given url */
export function getUrlQueries(search?: string): QueryMap {
  search = search ?? (globalThis as any).location?.href;

  return (search ?? "")
    .slice(1)
    .split("&")
    .reduce((mapping, query) => {
      if (query) {
        const [key, value] = query.split("=");
        mapping[key] = decodeURIComponent(value);
      }

      return mapping;
    }, {} as QueryMap);
}

/** Converts an object with query parameters into a search string */
export function stringifyQuery(obj: QueryMap): string {
  var query = Object.entries(obj)
    .filter((entry): entry is [string, string] => {
      const [_key, val] = entry;
      return val !== "" && val !== null && val !== undefined;
    })
    .map(([key, val]) => key + "=" + encodeURIComponent(val))
    .join("&");

  return query.length > 0 ? "?" + query : "";
}

/** It adds new query parameters to a URL */
export function addQueries(url: string, obj: QueryMap): string {
  const [path, queries = ""] = url.split("?");

  return (
    path +
    stringifyQuery({
      ...getUrlQueries("?" + queries),
      ...obj,
    })
  );
}

export function getDomainLevel(level: 1 | 2 | 3, host?: string) {
  return (host || (globalThis as any)?.location?.hostname)
    .split(".")
    .slice(-1 * level)
    .join(".");
}

export type AppRoute = { dev: string; domain: string };

export type AppRoutes = {
  accounts: AppRoute;
  admin: AppRoute;
  go: AppRoute;
  dashboard: AppRoute;
  myCompany: AppRoute;
  report: AppRoute;
  open: AppRoute;
  gogetter: AppRoute;
  haydayApp: AppRoute;
  theApp: AppRoute;
};

export const getApps: (host?: string) => AppRoutes = host => {
  const domain = host || getDomainLevel(2);
  const hostname = (globalThis as any).location?.hostname;

  const nachoBase = `http://${hostname === "localhost" ? "localhost:10091" : (globalThis as any).location?.host}`;

  return {
    accounts: { dev: `http://localhost:10004`, domain: `accounts.${domain}` },
    go: { dev: `http://localhost:10005`, domain: `go.${domain}` },
    admin: { dev: `http://0.0.0.0:4321/admin`, domain: `admin.${domain}/admin` },
    myCompany: { dev: `http://localhost:10008`, domain: `company.${domain}` },
    dashboard: { dev: `${nachoBase}/dashboard.html#`, domain: `dashboard.${domain}` },
    report: { dev: `${nachoBase}/report.html#`, domain: `report.${domain}` },
    open: { dev: `${nachoBase}/open.html#`, domain: `open.${domain}` },
    gogetter: { dev: `${nachoBase}/gogetter.html#`, domain: `gogetter.${domain}` },
    haydayApp: { dev: `${nachoBase}/app.html#`, domain: `app.${domain}` },
    theApp: { dev: `http://localhost:10100`, domain: `app.${domain}` },
  };
};

export type AppName = keyof AppRoutes;

/** Returns a url to another PINATA app under the same environment */
export const getAppUrl = (env: string = "development") => (host?: string) => (
  app: AppName,
  path: string = "",
  query: QueryMap = {},
): string => {
  const base = getBaseAppUrl(env)(host)(app);
  const queryStr = stringifyQuery({
    useStagingEnv: getUrlQueries().useStagingEnv,
    ...query,
  });

  return base + "/" + path + queryStr;
};

/** Returns the root url to a PINATA app under the same environment  */
export const getBaseAppUrl = (env: string) => (host?: string) => (app: AppName): string => {
  const apps = getApps(host);

  if (env === "development") {
    return apps[app].dev;
  } else {
    return "https://" + addEnvToDomain(env)(apps[app].domain, host);
  }
};

/** Adds an environment's domain to an app's domain */
export const addEnvToDomain = (environment: string) => (domain: string, host?: string) => {
  if (environment.toLowerCase() === "production") {
    return domain;
  }
  const partialDomain = getDomainLevel(2, host);

  return domain.replace(`.${partialDomain}`, `-${environment}.${partialDomain}`);
};
